// Q9 S6 / D7 — Cross-section error surface at the review step.
//
// Spec (docs/specs/visit-report-per-section-validation.md §Server error
// → UI surfacing): "Modal listing each cross-section error with a 'Go
// to {section}' link that navigates the wizard to the offending step
// + scrolls to the field." We render inline at the top of the review
// step rather than a modal — the carer is already pausing at the
// review step to verify, the inline list keeps everything in flow.
//
// Cross-section errors fire at SUBMIT only (D7 7a — locked). They
// arrive from the server's submit response; the wizard hands them to
// this component along with a navigate handler that switches the
// current step + sets a focusFieldName ref consumed by the section
// on mount.

import type { JSX } from "react";
import type { SectionStep } from "../lib/visit-report-conditional-rules.js";

const SECTION_LABELS: Record<SectionStep, string> = {
  vitals:     "Vitals",
  mobility:   "Mobility",
  clinical:   "Clinical",
  wellbeing:  "Wellbeing",
  medication: "Medication",
  notes:      "Notes",
};

export interface CrossSectionError {
  /** The error message shown to the carer. */
  message: string;
  /** Fields involved in the rule, with the section that owns each. */
  involvedFields: ReadonlyArray<{ fieldName: string; section: SectionStep; label?: string }>;
}

interface Props {
  errors:        readonly CrossSectionError[];
  onNavigateToField: (section: SectionStep, fieldName: string) => void;
}

export function CrossSectionErrorList({ errors, onNavigateToField }: Props): JSX.Element | null {
  if (errors.length === 0) return null;

  return (
    <div
      role="alert"
      data-testid="cross-section-error-list"
      className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4"
    >
      <h3 className="mb-2 text-sm font-semibold text-red-800">
        Some fields need attention before submitting
      </h3>
      <ul className="space-y-3">
        {errors.map((err, i) => (
          <li key={i} data-testid={`cross-section-error-${i}`} className="text-sm text-red-700">
            <p>{err.message}</p>
            {err.involvedFields.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {err.involvedFields.map((f) => (
                  <button
                    key={`${f.section}-${f.fieldName}`}
                    type="button"
                    onClick={() => onNavigateToField(f.section, f.fieldName)}
                    data-testid={`cross-section-goto-${f.section}-${f.fieldName}`}
                    className="rounded border border-red-300 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Go to {f.label ?? f.fieldName} ({SECTION_LABELS[f.section]})
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
